import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import ColorPaletteInspiration from './ColorPaletteInspiration';

const YARN_WEIGHTS = [
  {
    id: 0,
    name: "Lace",
    category: "Lace / Thread",
    gauge: "32–42 dc per 4\"",
    hook: "Steel 6–8 / B/1 (1.4–2.25mm)",
    yardage: "1,000+ yds / 100g",
    bestFor: "Doilies, lace shawls, fine details",
  },
  {
    id: 1,
    name: "Super Fine",
    category: "Fingering / Sock",
    gauge: "21–32 sts per 4\"",
    hook: "B/1–E/4 (2.25–3.5mm)",
    yardage: "400–500 yds / 100g",
    bestFor: "Socks, baby items, lightweight shawls",
  },
  {
    id: 2,
    name: "Fine",
    category: "Sport / Baby",
    gauge: "16–20 sts per 4\"",
    hook: "E/4–7 (3.5–4.5mm)",
    yardage: "300–400 yds / 100g",
    bestFor: "Baby clothes, light sweaters, summer tops",
  },
  {
    id: 3,
    name: "Light",
    category: "DK / Light Worsted",
    gauge: "12–17 sts per 4\"",
    hook: "7–I/9 (4.5–5.5mm)",
    yardage: "250–300 yds / 100g",
    bestFor: "Garments, accessories, light blankets",
  },
  {
    id: 4,
    name: "Medium",
    category: "Worsted / Aran",
    gauge: "11–14 sts per 4\"",
    hook: "I/9–K/10.5 (5.5–6.5mm)",
    yardage: "200–250 yds / 100g",
    bestFor: "Afghans, sweaters, hats, scarves — everything!",
  },
  {
    id: 5,
    name: "Bulky",
    category: "Chunky",
    gauge: "8–11 sts per 4\"",
    hook: "K/10.5–M/13 (6.5–9mm)",
    yardage: "150–200 yds / 100g",
    bestFor: "Quick blankets, cowls, winter accessories",
  },
  {
    id: 6,
    name: "Super Bulky",
    category: "Super Chunky",
    gauge: "7–9 sts per 4\"",
    hook: "M/13–Q (9–15mm)",
    yardage: "100–150 yds / 100g",
    bestFor: "Statement blankets, chunky cowls, floor poufs",
  },
  {
    id: 7,
    name: "Jumbo",
    category: "Roving",
    gauge: "6 or fewer sts per 4\"",
    hook: "Q and larger (15mm+)",
    yardage: "50–100 yds / 100g",
    bestFor: "Extreme blankets, art pieces, arm crochet",
  },
];

const CARE_SYMBOLS = [
  { symbol: "♻", label: "Machine wash cold" },
  { symbol: "30°", label: "Wash at 30°C / 86°F" },
  { symbol: "✕ ♻", label: "Do not machine wash — hand wash only" },
  { symbol: "□", label: "Tumble dry low" },
  { symbol: "✕ □", label: "Do not tumble dry — lay flat to dry" },
  { symbol: "▲", label: "Iron on low heat" },
  { symbol: "✕ △", label: "Do not iron" },
  { symbol: "○", label: "Dry clean" },
  { symbol: "✕ ○", label: "Do not dry clean" },
];

function YarnHookGuide({ colors, fonts, isAdmin }) {
  const [activeTab, setActiveTab] = useState("palettes");
  const [brands, setBrands] = useState({}); // { weightName: [{ id, yarn_library_id, brand_name, colorway_name, brand_url, image_url }] }
  const [editingWeight, setEditingWeight] = useState(null);
  const [saving, setSaving] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryResults, setLibraryResults] = useState([]);

  const loadBrands = async () => {
    const { data } = await supabase
      .from('yarn_guide_picks')
      .select('id, weight_name, display_order, yarn_library_id, yarn_library(brand_name, colorway_name, brand_url, image_url)')
      .order('display_order', { ascending: true });
    if (data) {
      const grouped = {};
      data.forEach(b => {
        if (!grouped[b.weight_name]) grouped[b.weight_name] = [];
        grouped[b.weight_name].push({
          id: b.id,
          yarn_library_id: b.yarn_library_id,
          brand_name: b.yarn_library?.brand_name,
          colorway_name: b.yarn_library?.colorway_name,
          brand_url: b.yarn_library?.brand_url,
          image_url: b.yarn_library?.image_url,
        });
      });
      setBrands(grouped);
    }
  };

  useEffect(() => { loadBrands(); }, []);

  const handleAddBrand = async (weightName, yarn) => {
    setSaving(true);
    const { data, error } = await supabase
      .from('yarn_guide_picks')
      .insert({ weight_name: weightName, yarn_library_id: yarn.id })
      .select('id, weight_name, yarn_library_id')
      .single();
    if (error) {
      console.error('yarn_guide_picks insert error:', error);
      alert(`Could not save: ${error.message}`);
    } else if (data) {
      setBrands(prev => ({
        ...prev,
        [weightName]: [...(prev[weightName] || []), {
          id: data.id,
          yarn_library_id: yarn.id,
          brand_name: yarn.brand_name,
          colorway_name: yarn.colorway_name,
          brand_url: yarn.brand_url,
          image_url: yarn.image_url,
        }],
      }));
    }
    setSaving(false);
  };

  const handleDeleteBrand = async (weightName, pickId) => {
    await supabase.from('yarn_guide_picks').delete().eq('id', pickId);
    setBrands(prev => ({
      ...prev,
      [weightName]: (prev[weightName] || []).filter(b => b.id !== pickId),
    }));
  };

  // Debounced library search
  useEffect(() => {
    if (!librarySearch.trim()) { setLibraryResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('yarn_library')
        .select('id, brand_name, colorway_name, brand_url, image_url')
        .or(`brand_name.ilike.%${librarySearch}%,colorway_name.ilike.%${librarySearch}%`)
        .limit(8);
      setLibraryResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [librarySearch]);

  const tabStyle = (id) => ({
    padding: "1rem 1.5rem",
    background: "none",
    border: "none",
    borderBottom: `3px solid ${activeTab === id ? colors.sageGreen : "transparent"}`,
    color: activeTab === id ? colors.sageGreen : colors.brownGray,
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "-2px",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
  });

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "2.5rem", color: colors.charcoal, marginBottom: "0.5rem", fontFamily: fonts.display }}>
          Yarn
        </h1>
      </div>

      {/* Tabs */}
      <div style={{
        display: "flex",
        gap: "0.5rem",
        marginBottom: "2rem",
        borderBottom: `2px solid ${colors.borderGreige}`,
        flexWrap: "wrap",
      }}>
        {[
          { id: "palettes", label: "Color Palettes" },
          { id: "yarn", label: "Yarn Guide" },
          { id: "label", label: "Yarn Labels" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={tabStyle(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* YARN GUIDE TAB */}
      {activeTab === "yarn" && (
        <div>
          <div style={{
            background: colors.bgCream,
            padding: "1.25rem 1.5rem",
            borderRadius: "12px",
            marginBottom: "2rem",
            border: `2px solid ${colors.borderGreige}`,
          }}>
            <p style={{ fontSize: "0.95rem", color: colors.darkGray, margin: 0 }}>
              Yarn weights range from 0 (finest) to 7 (thickest). The gauge and hook sizes below are typical starting points — always make a swatch!
            </p>
          </div>

          <div style={{
            background: "#fff",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: `2px solid ${colors.borderGreige}`,
            overflowX: "auto",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.borderGreige}` }}>
                  {["Weight", "Gauge (per 4\")", "Recommended Hook", "Yardage / 100g", "Best For", "Popular Brands"].map(h => (
                    <th key={h} style={{
                      padding: "1rem",
                      textAlign: "left",
                      fontSize: "0.85rem",
                      fontWeight: "700",
                      color: colors.brownGray,
                      whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                  {isAdmin && <th style={{ padding: "1rem", fontSize: "0.85rem", fontWeight: "700", color: colors.brownGray }}>Edit Brands</th>}
                </tr>
              </thead>
              <tbody>
                {YARN_WEIGHTS.map((w, idx) => {
                  const weightBrands = brands[w.name] || [];
                  const isEditingThis = editingWeight === w.name;
                  return (
                    <tr key={w.id} style={{ borderBottom: `1px solid ${colors.borderGreige}`, background: idx % 2 === 0 ? "#fff" : colors.bgCream, verticalAlign: "top" }}>
                      {/* Weight */}
                      <td style={{ padding: "1rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                          <div style={{
                            width: "32px", height: "32px",
                            background: colors.sageGreen,
                            color: "#fff",
                            borderRadius: "50%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "0.9rem", fontWeight: "700", flexShrink: 0,
                          }}>{w.id}</div>
                          <div>
                            <div style={{ fontWeight: "700", color: colors.charcoal, fontSize: "0.95rem" }}>{w.name}</div>
                            <div style={{ fontSize: "0.8rem", color: colors.brownGray }}>{w.category}</div>
                          </div>
                        </div>
                      </td>
                      {/* Gauge */}
                      <td style={{ padding: "1rem", fontSize: "0.9rem", color: colors.charcoal }}>{w.gauge}</td>
                      {/* Hook */}
                      <td style={{ padding: "1rem", fontSize: "0.9rem", color: colors.charcoal, whiteSpace: "nowrap" }}>{w.hook}</td>
                      {/* Yardage */}
                      <td style={{ padding: "1rem", fontSize: "0.9rem", color: colors.charcoal, whiteSpace: "nowrap" }}>{w.yardage}</td>
                      {/* Best For */}
                      <td style={{ padding: "1rem", fontSize: "0.85rem", color: colors.darkGray, minWidth: "160px" }}>{w.bestFor}</td>
                      {/* Brands */}
                      <td style={{ padding: "1rem", minWidth: "160px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                          {weightBrands.length === 0 && !isAdmin && (
                            <span style={{ fontSize: "0.8rem", color: colors.brownGray, fontStyle: "italic" }}>—</span>
                          )}
                          {weightBrands.map(b => (
                            <span key={b.id} style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                              {b.brand_url ? (
                                <a href={b.brand_url} target="_blank" rel="noopener noreferrer" style={{
                                  fontSize: "0.82rem", color: colors.sageGreen, fontWeight: "600",
                                  textDecoration: "none", borderBottom: `1px solid ${colors.sageGreen}`,
                                }}>
                                  {b.brand_name}
                                </a>
                              ) : (
                                <span style={{ fontSize: "0.82rem", color: colors.darkGray, fontWeight: "600" }}>{b.brand_name}</span>
                              )}
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteBrand(w.name, b.id)}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: colors.dustyRust, fontSize: "0.7rem", padding: "0 2px", lineHeight: 1 }}
                                  title="Remove brand"
                                >✕</button>
                              )}
                            </span>
                          ))}
                        </div>
                        {isAdmin && isEditingThis && (
                          <div style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "210px" }}>
                            <input
                              placeholder="Search yarn library…"
                              value={librarySearch}
                              onChange={e => setLibrarySearch(e.target.value)}
                              style={{ fontSize: "0.8rem", padding: "0.3rem 0.5rem", border: `1px solid ${colors.sageGreen}`, borderRadius: "6px", width: "200px" }}
                            />
                            {libraryResults.length > 0 && (
                              <div style={{ background: "#fff", border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", overflow: "hidden", maxHeight: "200px", overflowY: "auto" }}>
                                {libraryResults.map(r => (
                                  <div
                                    key={r.id}
                                    onClick={() => { handleAddBrand(w.name, r); setLibrarySearch(""); setLibraryResults([]); }}
                                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.6rem", cursor: "pointer", borderBottom: `1px solid ${colors.borderGreige}` }}
                                    onMouseEnter={e => e.currentTarget.style.background = colors.bgCream}
                                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                  >
                                    {r.image_url && <img src={r.image_url} alt="" style={{ width: "28px", height: "28px", objectFit: "cover", borderRadius: "4px", flexShrink: 0 }} />}
                                    <div>
                                      <div style={{ fontSize: "0.78rem", fontWeight: "600", color: colors.charcoal }}>{r.brand_name}</div>
                                      <div style={{ fontSize: "0.72rem", color: colors.brownGray }}>{r.colorway_name}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <button
                              onClick={() => { setEditingWeight(null); setLibrarySearch(""); setLibraryResults([]); }}
                              style={{ fontSize: "0.78rem", padding: "0.3rem 0.6rem", background: "transparent", color: colors.brownGray, border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", cursor: "pointer", alignSelf: "flex-start" }}
                            >Done</button>
                          </div>
                        )}
                      </td>
                      {/* Admin edit column */}
                      {isAdmin && (
                        <td style={{ padding: "1rem" }}>
                          {!isEditingThis ? (
                            <button
                              onClick={() => { setEditingWeight(w.name); setLibrarySearch(""); setLibraryResults([]); }}
                              style={{ fontSize: "0.78rem", padding: "0.3rem 0.7rem", background: colors.bgCream, color: colors.brownGray, border: `1px solid ${colors.borderGreige}`, borderRadius: "6px", cursor: "pointer" }}
                            >+ Add Brand</button>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* READING A YARN LABEL TAB */}
      {activeTab === "label" && (
        <div>
          <div style={{
            background: colors.bgCream,
            padding: "1.25rem 1.5rem",
            borderRadius: "12px",
            marginBottom: "2rem",
            border: `2px solid ${colors.borderGreige}`,
          }}>
            <p style={{ fontSize: "0.95rem", color: colors.darkGray, margin: 0 }}>
              Every yarn label is packed with information. Here's how to decode it.
            </p>
          </div>

          <div style={{ display: "flex", gap: "3rem", flexWrap: "wrap", alignItems: "flex-start" }}>
            {/* Label Diagram — single portrait card matching real ball band format */}
            <div style={{ flex: "0 0 auto" }}>
              <div style={{
                width: "220px",
                background: "#fff",
                borderRadius: "4px",
                boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
                border: "1px solid #bbb",
                fontFamily: "Arial, sans-serif",
                overflow: "hidden",
              }}>
                {/* Brand + address */}
                <div style={{ padding: "0.5rem 0.75rem 0.25rem", textAlign: "center", borderBottom: "1px solid #eee" }}>
                  <div style={{ fontSize: "0.5rem", color: "#aaa", marginBottom: "0.15rem" }}>CA00111 · P.O. Box 40, Lionized, ON N4W 3G</div>
                  <div style={{ fontSize: "1rem", fontWeight: "900", color: "#111", letterSpacing: "0.1em" }}>BERNAT®</div>
                </div>

                {/* Dye lot + barcode */}
                <div style={{ padding: "0.4rem 0.75rem 0.3rem", textAlign: "center", borderBottom: "1px solid #eee" }}>
                  <div style={{ fontSize: "0.65rem", color: "#333", fontWeight: "600", marginBottom: "0.2rem" }}>2021-01-027</div>
                  <div style={{ display: "flex", gap: "1px", justifyContent: "center", height: "28px", alignItems: "stretch", marginBottom: "0.15rem" }}>
                    {[1,2,1,1,3,1,2,1,1,2,1,3,1,1,2,1,2,1,3,1,1,2,1,2,1,1,3,1,2,1].map((w, i) => (
                      <div key={i} style={{ width: `${w * 1.5}px`, background: "#111" }} />
                    ))}
                  </div>
                  <div style={{ fontSize: "0.5rem", color: "#555", letterSpacing: "0.05em" }}>0 57355 43759 3</div>
                </div>

                {/* Color name + dye lot number */}
                <div style={{ padding: "0.5rem 0.75rem", textAlign: "center", borderBottom: "1px solid #eee" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "#111" }}>36001</div>
                  <div style={{ fontSize: "0.8rem", fontWeight: "600", color: "#333" }}>CHARCOAL OMBRE</div>
                  <div style={{ fontSize: "0.65rem", color: "#777" }}>Charbon Ombré</div>
                  <div style={{ fontSize: "0.6rem", color: "#aaa", marginTop: "0.2rem" }}>161036</div>
                </div>

                {/* Gauge section — weight symbol + knit + crochet boxes */}
                <div style={{ padding: "0.5rem 0.6rem", borderBottom: "1px solid #eee", display: "flex", gap: "0.35rem", alignItems: "center", justifyContent: "center" }}>
                  {/* CYC weight symbol box */}
                  <div style={{ border: "1.5px solid #333", borderRadius: "3px", padding: "0.25rem 0.3rem", textAlign: "center", minWidth: "44px" }}>
                    <div style={{ fontSize: "0.42rem", fontWeight: "700", color: "#333", letterSpacing: "0.03em" }}>SUPER BULKY</div>
                    <div style={{ fontSize: "1.3rem", fontWeight: "900", color: "#111", lineHeight: 1, margin: "0.1rem 0" }}>6</div>
                    <div style={{ fontSize: "0.38rem", color: "#555" }}>Très épais</div>
                    <div style={{ fontSize: "0.38rem", color: "#555" }}>Super abultado</div>
                  </div>
                  {/* Knit gauge box */}
                  <div style={{ border: "1.5px solid #333", borderRadius: "3px", padding: "0.25rem 0.35rem", textAlign: "center", fontSize: "0.55rem" }}>
                    <div style={{ color: "#555", marginBottom: "0.15rem" }}>10 × 10 cm</div>
                    <div style={{ fontWeight: "700", color: "#111", fontSize: "0.65rem" }}>8 mm</div>
                    <div style={{ color: "#777" }}>4 × 4 in</div>
                    {/* Knitting needles icon — 2 crossed needles */}
                    <svg width="16" height="10" viewBox="0 0 16 10" style={{ display: "block", margin: "0.15rem auto" }}>
                      <line x1="1" y1="2" x2="15" y2="9" stroke="#333" strokeWidth="1.4" strokeLinecap="round"/>
                      <line x1="1" y1="9" x2="15" y2="2" stroke="#333" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                    <div style={{ color: "#555" }}>U.S. 11</div>
                    <div style={{ fontWeight: "700", color: "#111" }}>13 R</div>
                    <div style={{ color: "#555" }}>8 Sts</div>
                  </div>
                  {/* Crochet gauge box */}
                  <div style={{ border: "1.5px solid #333", borderRadius: "3px", padding: "0.25rem 0.35rem", textAlign: "center", fontSize: "0.55rem" }}>
                    <div style={{ color: "#555", marginBottom: "0.15rem" }}>10 × 10 cm</div>
                    <div style={{ fontWeight: "700", color: "#111", fontSize: "0.65rem" }}>8 mm</div>
                    <div style={{ color: "#777" }}>4 × 4 in</div>
                    {/* Crochet hook icon */}
                    <svg width="16" height="10" viewBox="0 0 16 10" style={{ display: "block", margin: "0.15rem auto" }}>
                      <line x1="2" y1="9" x2="14" y2="2" stroke="#333" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M2,9 Q1,7 3,7" fill="none" stroke="#333" strokeWidth="1.2"/>
                    </svg>
                    <div style={{ color: "#555" }}>U.S. L/11</div>
                    <div style={{ fontWeight: "700", color: "#111" }}>8 R</div>
                    <div style={{ color: "#555" }}>7 Sc</div>
                  </div>
                </div>

                {/* Care symbols — ISO style SVGs */}
                <div style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid #eee", textAlign: "center" }}>
                  <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", alignItems: "center", marginBottom: "0.4rem" }}>
                    {/* Machine wash tub */}
                    <svg width="22" height="19" viewBox="0 0 22 19">
                      <path d="M2,8 L2,17 Q2,18 3,18 L19,18 Q20,18 20,17 L20,8 Z" fill="none" stroke="#333" strokeWidth="1.4"/>
                      <path d="M0,8 L22,8" stroke="#333" strokeWidth="1.4"/>
                      <text x="11" y="15" textAnchor="middle" fontSize="5" fontWeight="bold" fill="#333">30</text>
                    </svg>
                    {/* No bleach — triangle with X */}
                    <svg width="20" height="19" viewBox="0 0 20 19">
                      <polygon points="10,1 19,17 1,17" fill="none" stroke="#333" strokeWidth="1.4"/>
                      <line x1="5" y1="8" x2="15" y2="16" stroke="#333" strokeWidth="1.4"/>
                      <line x1="15" y1="8" x2="5" y2="16" stroke="#333" strokeWidth="1.4"/>
                    </svg>
                    {/* Tumble dry — circle with dot */}
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <circle cx="10" cy="10" r="8.5" fill="none" stroke="#333" strokeWidth="1.4"/>
                      <circle cx="10" cy="10" r="2.5" fill="#333"/>
                    </svg>
                    {/* Do not dry flat — square with X */}
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <rect x="1" y="1" width="18" height="18" rx="1" fill="none" stroke="#333" strokeWidth="1.4"/>
                      <line x1="4" y1="4" x2="16" y2="16" stroke="#333" strokeWidth="1.4"/>
                      <line x1="16" y1="4" x2="4" y2="16" stroke="#333" strokeWidth="1.4"/>
                    </svg>
                    {/* Do not dry clean — circle with X */}
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <circle cx="10" cy="10" r="8.5" fill="none" stroke="#333" strokeWidth="1.4"/>
                      <line x1="4" y1="4" x2="16" y2="16" stroke="#333" strokeWidth="1.4"/>
                      <line x1="16" y1="4" x2="4" y2="16" stroke="#333" strokeWidth="1.4"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: "0.52rem", color: "#555" }}>Machine washable and dryable.</div>
                  <div style={{ fontSize: "0.52rem", color: "#888" }}>Lavable et séchable à la machine.</div>
                </div>

                {/* Fiber content */}
                <div style={{ padding: "0.4rem 0.75rem", textAlign: "center", borderBottom: "1px solid #eee" }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: "700", color: "#111" }}>100% Polyester / Polyester / Poliéster</div>
                </div>

                {/* Weight + yardage */}
                <div style={{ padding: "0.5rem 0.75rem 0.6rem", textAlign: "center" }}>
                  <div style={{ fontSize: "0.5rem", color: "#aaa", letterSpacing: "0.06em", marginBottom: "0.2rem" }}>NET WEIGHT · POIDS NET · PESO NETO</div>
                  <div style={{ fontSize: "1.1rem", fontWeight: "700", color: "#111" }}>10.5 oz / 300 g</div>
                  <div style={{ fontSize: "0.75rem", color: "#444", marginTop: "0.15rem" }}>220 yds / vgs / 201 m</div>
                </div>
              </div>
            </div>

            {/* Callouts */}
            <div style={{ flex: 1, minWidth: "260px", display: "flex", flexDirection: "column", gap: "1rem" }}>
              {[
                {
                  color: colors.sageGreen,
                  title: "Brand",
                  label: "e.g. Bernat",
                  desc: "The manufacturer. Different brands of the same weight can feel very different — fiber, texture, and quality vary a lot.",
                },
                {
                  color: colors.dustyRust,
                  title: "Dye Lot",
                  label: "e.g. 027",
                  desc: "Often found directly above the barcode, it will look \"stamped\" on the label rather than printed. It may also be labeled Lot, L, or DLOT. Colors vary slightly between dye batches — always buy enough from the same lot for your whole project.",
                },
                {
                  color: colors.mustard,
                  title: "Colorway",
                  label: "e.g. Charcoal Ombre",
                  desc: "The specific color name or number. If you need more yarn mid-project, match both the colorway AND dye lot — same colorway from a different lot can look slightly off.",
                },
                {
                  color: "#7c9eb2",
                  title: "Weight Symbol",
                  label: "e.g. 6 — Super Bulky",
                  desc: "The number (0–7) tells you the thickness category. Use this when substituting yarn or picking your hook. The two boxes next to it show gauge for knitting (crossed needles) and crochet (single hook) separately.",
                },
                {
                  color: colors.sageGreen,
                  title: "Gauge Recommendation",
                  label: "e.g. 8mm hook · 7 sc × 8 rows = 4\"",
                  desc: "The crochet gauge is indicated by the single hook icon. This label shows 7 sc and 8 rows over a 4\" (10cm) square using an 8mm hook. Always swatch first — your tension may differ.",
                },
                {
                  color: colors.mustard,
                  title: "Care Instructions",
                  label: "symbols row below the gauge",
                  desc: "ISO symbols tell you how to wash and dry. A ✕ through any symbol means don't do that. See the Care Symbol Key below for a full reference.",
                },
                {
                  color: colors.dustyRust,
                  title: "Fiber Content",
                  label: "e.g. 100% Polyester",
                  desc: "What the yarn is made of. Polyester / Acrylic = durable & machine washable. Wool = warm & elastic. Cotton = breathable, no stretch. Blends combine benefits.",
                },
                {
                  color: "#7c9eb2",
                  title: "Yardage",
                  label: "e.g. 220 yds / 201 m",
                  desc: "Total length in the skein — this is the number to use when calculating how many skeins you need, not the weight in oz/g.",
                },
              ].map((item, i) => (
                <div key={i} style={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "flex-start",
                  padding: "0.85rem 1rem",
                  background: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  border: `2px solid ${colors.borderGreige}`,
                  borderLeft: `4px solid ${item.color}`,
                }}>
                  <div>
                    <div style={{ fontWeight: "700", fontSize: "0.95rem", color: colors.charcoal, marginBottom: "0.15rem" }}>{item.title}</div>
                    {item.label && <div style={{ fontSize: "0.78rem", color: colors.sageGreen, fontWeight: "600", marginBottom: "0.25rem" }}>{item.label}</div>}
                    <div style={{ fontSize: "0.875rem", color: colors.darkGray, lineHeight: "1.5" }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Care Symbol Key */}
          <div style={{
            marginTop: "3rem",
            background: "#fff",
            borderRadius: "16px",
            padding: "1.5rem 2rem",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: `2px solid ${colors.borderGreige}`,
          }}>
            <h3 style={{ fontSize: "1.1rem", color: colors.charcoal, fontWeight: "700", marginBottom: "1.25rem" }}>
              Care Symbol Key
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.75rem" }}>
              {CARE_SYMBOLS.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <div style={{
                    width: "36px", height: "36px",
                    background: colors.bgCream,
                    border: `1px solid ${colors.borderGreige}`,
                    borderRadius: "6px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.75rem", fontWeight: "700", color: colors.charcoal, flexShrink: 0,
                    textAlign: "center",
                  }}>{s.symbol}</div>
                  <span style={{ fontSize: "0.85rem", color: colors.darkGray }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COLOR PALETTES TAB */}
      {activeTab === "palettes" && (
        <div>
          <div style={{
            background: colors.bgCream,
            padding: "1.25rem 1.5rem",
            borderRadius: "12px",
            marginBottom: "2rem",
            border: `2px solid ${colors.borderGreige}`,
          }}>
            <p style={{ fontSize: "0.95rem", color: colors.darkGray, margin: 0 }}>
              Colorway combination inspiration, curated from popular yarn brands.
            </p>
          </div>
          <ColorPaletteInspiration colors={colors} fonts={fonts} isAdmin={isAdmin} />
        </div>
      )}
    </div>
  );
}

export default YarnHookGuide;
